import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { useApi, useStore } from '../../hooks/storeHook'
import { useBoolState } from '../../hooks/commonHooks'
import { useMessages } from '../../hooks/snackbarHooks'

import { WbAccountModel } from '../../server/mpsklad_core/Models/WbAccountModel'
import { OzonAccountModel } from '../../server/mpsklad_core/Models/OzonAccountModel'
import { YandexMarketAccountModel } from '../../server/mpsklad_core/Models/YandexMarketAccountModel'
import { WbDeliveryType } from '../../server/mpsklad_core/Entity/Wildberries/WbDeliveryType'
import { UpdateWbAccountAllowOptionRequest } from '../../server/mpsklad_core/Models/UpdateWbAccountAllowOptionRequest'
import { UpdateOzonAccountAllowOptionRequest } from '../../server/mpsklad_core/Models/UpdateOzonAccountAllowOptionRequest'
import { UpdateYandexMarketAccountAllowOptionRequest } from '../../server/mpsklad_core/Models/UpdateYandexMarketAccountAllowOptionRequest'

export interface UserSupplyAccountsModalProps {
  open: boolean

  onClose: () => void

  userId: string
}

type SupplyAccountState<T> = {
  account: T
  allowOption: boolean
}

type SupplyFormState = {
  wbAccounts: SupplyAccountState<WbAccountModel>[]
  ozonAccounts: SupplyAccountState<OzonAccountModel>[]
  yandexMarketAccounts: SupplyAccountState<YandexMarketAccountModel>[]
}

export const UserSupplyAccountsModal =
  observer(({open, onClose, userId}: UserSupplyAccountsModalProps) => {
    const {admin} = useApi()
    const store = useStore()
    const {showSuccess} = useMessages()
    const classes = useStyles()

    const [accounts, setAccounts] = useState<SupplyFormState | null>(null)
    const [isLoading, setLoading, setNotLoading] = useBoolState()
    const [isSaving, setSaving, setNotSaving] = useBoolState()

    const loadAccounts = useCallback(async () => {
      setLoading()
      try {
        const [wbAccounts, ozonAccounts, yandexMarketAccounts] = await Promise.all([
          admin.getUserWbAccounts(userId),
          admin.getUserOzonFboAccounts(userId),
          admin.getUserYandexMarketFbyAccounts(userId)
        ])

        // Filter FBW accounts (deliveryType = 0)
        const fbwAccounts = wbAccounts.filter(account => account.deliveryType === WbDeliveryType.FBW)

        // Ozon FBO and YandexMarket FBY accounts already filtered on backend

        setAccounts({
          wbAccounts: fbwAccounts.map(account => ({
            account,
            allowOption: account.allowOptionAutoCreateMoveDocsInMoySklad
          })),
          ozonAccounts: ozonAccounts.map(account => ({
            account,
            allowOption: account.allowOptionAutoCreateMoveDocsInMoySklad
          })),
          yandexMarketAccounts: yandexMarketAccounts.map(account => ({
            account,
            allowOption: account.allowOptionAutoCreateMoveDocsInMoySklad
          }))
        })
      } catch (e) {
        console.error('Failed to load accounts', e)
      } finally {
        setNotLoading()
      }
    }, [admin, userId, setLoading, setNotLoading])

    useEffect(() => {
      if (open) {
        loadAccounts().catch(console.error)
      }
    }, [open, loadAccounts])

    const handleToggleWb = (accountId: number) => {
      if (!accounts) {
        return
      }

      setAccounts({
        ...accounts,
        wbAccounts: accounts.wbAccounts.map(item =>
          item.account.id === accountId
          ? {...item, allowOption: !item.allowOption}
          : item
        )
      })
    }

    const handleToggleOzon = (accountId: number) => {
      if (!accounts) {
        return
      }

      setAccounts({
        ...accounts,
        ozonAccounts: accounts.ozonAccounts.map(item =>
          item.account.id === accountId
          ? {...item, allowOption: !item.allowOption}
          : item
        )
      })
    }

    const handleToggleYandexMarket = (accountId: number) => {
      if (!accounts) {
        return
      }

      setAccounts({
        ...accounts,
        yandexMarketAccounts: accounts.yandexMarketAccounts.map(item =>
          item.account.id === accountId
          ? {...item, allowOption: !item.allowOption}
          : item
        )
      })
    }

    const handleSave = async () => {
      if (!accounts || isSaving) {
        return
      }

      setSaving()
      try {
        // Prepare WB requests
        const wbRequests: UpdateWbAccountAllowOptionRequest[] = accounts.wbAccounts.map(item => ({
          accountId: item.account.id,
          allowOptionAutoCreateMoveDocsInMoySklad: item.allowOption
        }))

        // Prepare Ozon requests
        const ozonRequests: UpdateOzonAccountAllowOptionRequest[] = accounts.ozonAccounts.map(item => ({
          accountId: item.account.id,
          allowOptionAutoCreateMoveDocsInMoySklad: item.allowOption
        }))

        // Prepare YandexMarket requests
        const yandexMarketRequests: UpdateYandexMarketAccountAllowOptionRequest[] = accounts.yandexMarketAccounts.map(
          item => ({
            accountId: item.account.id,
            allowOptionAutoCreateMoveDocsInMoySklad: item.allowOption
          }))

        // Update all in parallel
        await Promise.all([
          wbRequests.length > 0 ? admin.updateWbAccountsAllowOption(wbRequests) : Promise.resolve(),
          ozonRequests.length > 0 ? admin.updateOzonAccountsAllowOption(ozonRequests) : Promise.resolve(),
          yandexMarketRequests.length > 0
          ? admin.updateYandexMarketAccountsAllowOption(yandexMarketRequests)
          : Promise.resolve()
        ])

        store.adminStore.users.reload()
        showSuccess('Настройки сохранены')
        onClose()
      } catch (e) {
        console.error('Failed to save account settings', e)
      } finally {
        setNotSaving()
      }
    }

    const handleClose = () => {
      if (!isSaving) {
        onClose()
      }
    }

    const hasAccounts = accounts && (accounts.wbAccounts.length > 0 || accounts.ozonAccounts.length > 0
                                     || accounts.yandexMarketAccounts.length > 0)

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="supply-accounts-dialog-title"
      >
        <DialogTitle id="supply-accounts-dialog-title">
          Настройки аккаунтов для поставок
        </DialogTitle>

        <DialogContent>
          {isLoading ? (
            <div className={classes.loading}>
              <CircularProgress/>
            </div>
          ) : hasAccounts ? (
            <>
              {/* Wildberries FBW Section */}
              {accounts.wbAccounts.length > 0 && (
                <>
                  <Typography variant="h6" className={classes.sectionHeader}>
                    Wildberries (FBW)
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Разрешить опцию автосоздания перемещений</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.wbAccounts.map(({account, allowOption}) => (
                        <TableRow key={`wb-${account.id}`}>
                          <TableCell>{account.name || `WB #${account.id}`}</TableCell>
                          <TableCell>
                            <Checkbox
                              checked={allowOption}
                              onChange={() => handleToggleWb(account.id)}
                              disabled={isSaving}
                              color="primary"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}

              {/* Ozon FBO Section */}
              {accounts.ozonAccounts.length > 0 && (
                <>
                  <Typography variant="h6" className={classes.sectionHeader}>
                    Ozon (FBO)
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Разрешить опцию автосоздания перемещений</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.ozonAccounts.map(({account, allowOption}) => (
                        <TableRow key={`ozon-${account.id}`}>
                          <TableCell>{account.name || `Ozon #${account.id}`}</TableCell>
                          <TableCell>
                            <Checkbox
                              checked={allowOption}
                              onChange={() => handleToggleOzon(account.id)}
                              disabled={isSaving}
                              color="primary"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}

              {/* YandexMarket FBY Section */}
              {accounts.yandexMarketAccounts.length > 0 && (
                <>
                  <Typography variant="h6" className={classes.sectionHeader}>
                    Yandex.Market (FBY)
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Разрешить опцию автосоздания перемещений</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.yandexMarketAccounts.map(({account, allowOption}) => (
                        <TableRow key={`ym-${account.id}`}>
                          <TableCell>{account.name || `YandexMarket #${account.id}`}</TableCell>
                          <TableCell>
                            <Checkbox
                              checked={allowOption}
                              onChange={() => handleToggleYandexMarket(account.id)}
                              disabled={isSaving}
                              color="primary"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          ) : (
                <Typography>У пользователя нет аккаунтов для управления поставками</Typography>
              )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary" disabled={isSaving}>
            Отмена
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={isSaving || !hasAccounts}
          >
            {isSaving ? <CircularProgress size={24} color="inherit"/> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  })

const useStyles = makeStyles((theme) => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px'
  },
  sectionHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600
  }
}))